import { useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  X, Home, Activity, Wallet, ShieldCheck, HeartPulse, Settings, ChevronRight, LogOut,
} from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '../../store/useAppStore';
import { useAuthStore } from '../../store/useAuthStore';
import { useEntitlements } from '../../hooks/useEntitlements';

const NAV_ITEMS = [
  { to: '/home',       icon: Home,        label: 'Home',       matchPrefix: '/home'       },
  { to: '/pulse/dashboard', icon: Activity, label: 'Pulse',    matchPrefix: '/pulse'      },
  { to: '/portfolio',  icon: Wallet,      label: 'Portfolio',  matchPrefix: '/portfolio'  },
  { to: '/validation', icon: ShieldCheck, label: 'Validation', matchPrefix: '/validation' },
  { to: '/health',     icon: HeartPulse,  label: 'Health',     matchPrefix: '/health'     },
  { to: '/settings',   icon: Settings,    label: 'Settings',   matchPrefix: '/settings'   },
];

const MODULE_ITEMS = [
  { id: 'diamondedge',  label: 'DiamondEdge',  subtitle: 'MLB Intelligence',    to: '/baseball/mlb/command-center', emoji: '⚾', available: true  },
  { id: 'gridironedge', label: 'GridironEdge', subtitle: 'NFL Intelligence',    to: null,                           emoji: '🏈', available: false },
  { id: 'courtedge',   label: 'CourtEdge',    subtitle: 'WNBA Intelligence',   to: null,                           emoji: '🏀', available: false },
  { id: 'iceedge',     label: 'IceEdge',      subtitle: 'NHL Intelligence',    to: null,                           emoji: '🏒', available: false },
  { id: 'pitchedge',   label: 'PitchEdge',    subtitle: 'Soccer Intelligence', to: null,                           emoji: '⚽', available: false },
];

function planLabel(plan: string): string {
  if (!plan || plan === 'free') return 'Free';
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

export function MobileDrawer() {
  const { mobileDrawerOpen, mobileDrawerTab, setMobileDrawerOpen } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuthStore((s) => ({ user: s.user, signOut: s.signOut }));
  const { entitlements } = useEntitlements();

  const userHandle = user?.email?.split('@')[0] ?? '';
  const userInitials = userHandle.slice(0, 2).toUpperCase();
  const plan = planLabel(entitlements?.plan ?? 'free');

  useEffect(() => {
    document.body.style.overflow = mobileDrawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileDrawerOpen]);

  if (!mobileDrawerOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm md:hidden"
        onClick={() => setMobileDrawerOpen(false)}
      />

      {/* Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 md:hidden animate-slide-up flex flex-col"
        style={{
          maxHeight: '88vh',
          background: 'var(--color-surface-1)',
          borderTop: '1px solid var(--color-surface-4)',
          borderRadius: '20px 20px 0 0',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-surface-4" />
        </div>

        {/* User profile header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-surface-4 flex-shrink-0">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand/40 to-brand/20 border border-brand/30 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-brand leading-none">{userInitials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-100 truncate">{userHandle}</p>
            <span className="text-[10px] font-bold uppercase tracking-wide text-gold px-1.5 py-0.5 rounded bg-gold/10 border border-gold/20 leading-none">
              {plan}
            </span>
          </div>
          <button
            onClick={() => { setMobileDrawerOpen(false); void signOut().then(() => navigate('/')); }}
            className="p-2 rounded-full text-gray-600 hover:text-loss hover:bg-surface-3 transition-colors flex-shrink-0"
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
          <button
            onClick={() => setMobileDrawerOpen(false)}
            className="p-2 rounded-full text-gray-500 hover:text-gray-300 hover:bg-surface-3 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab pills */}
        <div className="flex gap-1 px-4 pt-3 pb-2 flex-shrink-0">
          {(['nav', 'module'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setMobileDrawerOpen(true, tab)}
              className={clsx(
                'flex-1 py-2 rounded-lg text-sm font-semibold transition-colors',
                mobileDrawerTab === tab
                  ? 'bg-brand/15 text-brand'
                  : 'bg-surface-3 text-gray-400 hover:text-gray-200',
              )}
            >
              {tab === 'nav' ? 'Navigate' : 'Modules'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-3 pb-4">

          {/* ── Nav tab ── */}
          {mobileDrawerTab === 'nav' && (
            <nav className="space-y-0.5 pt-1">
              {NAV_ITEMS.map(({ to, icon: Icon, label, matchPrefix }) => {
                const active = location.pathname === to || location.pathname.startsWith(matchPrefix);
                return (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setMobileDrawerOpen(false)}
                    className={() => clsx(
                      'flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors',
                      active
                        ? 'bg-brand/12 text-brand'
                        : 'text-gray-400 hover:text-gray-200 hover:bg-surface-2',
                    )}
                  >
                    <div className={clsx(
                      'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                      active ? 'bg-brand/15 border border-brand/25' : 'bg-surface-3 border border-surface-4',
                    )}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="flex-1">{label}</span>
                    {active && <ChevronRight className="w-3.5 h-3.5 opacity-50 flex-shrink-0" />}
                  </NavLink>
                );
              })}
            </nav>
          )}

          {/* ── Modules tab ── */}
          {mobileDrawerTab === 'module' && (
            <div className="space-y-2 pt-1">
              {MODULE_ITEMS.map(({ id, label, subtitle, to, emoji, available }) => (
                <button
                  key={id}
                  onClick={() => {
                    if (available && to) {
                      setMobileDrawerOpen(false);
                      void navigate(to);
                    }
                  }}
                  disabled={!available}
                  className={clsx(
                    'w-full flex items-center gap-3 px-3 py-3.5 rounded-xl text-left transition-all',
                    available
                      ? 'bg-surface-1 border border-surface-3 hover:border-surface-5 hover:bg-surface-2 active:scale-[0.98]'
                      : 'bg-surface-1 border border-surface-3 opacity-40 cursor-not-allowed',
                  )}
                >
                  <div className="w-10 h-10 rounded-xl bg-surface-3 border border-surface-4 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl leading-none">{emoji}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-100">{label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
                  </div>
                  {available ? (
                    <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-lg bg-surface-3 text-gray-600 flex-shrink-0">
                      Soon
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

        </div>
      </div>
    </>
  );
}
